import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { Card, Form, Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useAuth } from "../../../providers/authprovider";
import { faCalendarDay, faFilter, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { MALFUNCTION_STATES,STATE_DENIED, getMalfunctionStatesInfo } from "../StatusModal";
import CalendarEventsResults from "./CalendarEventsResults";

const STATUS_OPTIONS = Object.keys(MALFUNCTION_STATES).map((state) => ({
  value: state,
  label: getMalfunctionStatesInfo(state).name,
})).concat({ value: STATE_DENIED, label: getMalfunctionStatesInfo(STATE_DENIED).name }); // allow empty status

const SORT_BY_OPTIONS = [
  { value: "date", label: "Data" },
  { value: "building", label: "Nazwa obiektu" },
];

const SORT_DIR_OPTIONS = [
  { value: "asc", label: "Rosnąco" },
  { value: "desc", label: "Malejąco" },
];

function formatDateYYYYMMDD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeResponse(apiResult) {
  const payload = apiResult?.content ?? apiResult;
  if (payload?.items) return payload;
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, hasMore: false, page: 1, limit: 20 };
  }
  return { items: [], total: 0, hasMore: false, page: 1, limit: 20 };
}

export default function CalendarEventsByStatusAndDate({ privilege }) {
  const today = useMemo(() => formatDateYYYYMMDD(new Date()), []);
  const LIMIT = 20;

  const {
    calendar: { loadEventsToDateWithStatus },
  } = useAuth();

  // filters
  const [status, setStatus] = useState("");
  const [toDate, setToDate] = useState(today);

  // NEW: search + sorting
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");

  // results state
  const [phase, setPhase] = useState("idle"); // idle | loading | success | empty | error
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollYBeforeLoadRef = useRef(null);
  const [error, setError] = useState(null);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // prevent parallel loadMore calls
  const loadingMoreRef = useRef(false);

  const getStatusParam = () => (status === "" ? "NONE" : status);
  const getSearchParam = () => search.trim(); // backend: paging.q

  const loadPage = async (nextPage, mode /* "initial" | "more" */) => {
    const statusParam = getStatusParam();

    if (mode === "initial") {
      setPhase("loading");
      setError(null);
      setItems([]);
      setPage(1);
      setHasMore(false);
      setTotal(0);
    } else {
      setIsLoadingMore(true);
      scrollYBeforeLoadRef.current = window.scrollY;
    }

    try {
      const result = await loadEventsToDateWithStatus({
        dateTo: toDate,
        status: statusParam,
        page: nextPage,
        limit: LIMIT,
        q: getSearchParam(),
        sortBy,
        sortDir,
      });

      const normalized = normalizeResponse(result);
      const newItems = Array.isArray(normalized.items) ? normalized.items : [];
      const newTotal = Number(normalized.total ?? 0);
      const newHasMore = Boolean(normalized.hasMore);

      if (mode === "more") {
        setItems((prev) => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }

      setTotal(newTotal);
      setHasMore(newHasMore);
      setPage(nextPage);

      const finalCount = mode === "more" ? items.length + newItems.length : newItems.length;
      if (finalCount === 0) setPhase("empty");
      else setPhase("success");
    } catch (err) {
      setPhase("error");
      setError(err?.message ?? String(err));
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await loadPage(1, "initial");
  };

  const handleReset = () => {
    setStatus("");
    setToDate(today);
    setSearch("");
    setSortBy("date");
    setSortDir("asc");

    setPhase("idle");
    setError(null);
    setItems([]);
    setPage(1);
    setHasMore(false);
    setTotal(0);
    loadingMoreRef.current = false;
  };

  useLayoutEffect(() => {
    if (!isLoadingMore && scrollYBeforeLoadRef.current != null) {
      window.scrollTo(0, scrollYBeforeLoadRef.current);
      scrollYBeforeLoadRef.current = null;
    }
  }, [isLoadingMore, items.length]);

  const handleLoadMore = async () => {
    if (phase !== "success") return;
    if (!hasMore) return;
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    await loadPage(page + 1, "more");
  };

  const selectedStatusLabel =
    status === "" ? "Brak statusu" : STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;

  const handleDeleteRow = ({ id }) => {
    setItems((prev) => prev.filter((e) => e.id !== id));
    setTotal((t) => Math.max(0, (Number(t) || 0) - 1));
  };

  const handleEventChanged = (updatedEvent) => {
    if (!updatedEvent?.id) return;

    setItems((prev) => {
      const idx = prev.findIndex((e) => e.id === updatedEvent.id);
      if (idx === -1) return prev;
      const merged = { ...prev[idx], ...updatedEvent };
      const next = prev.slice();
      next[idx] = merged;
      return next;
    });
  };

  const resultsData = { items, total, hasMore, page, limit: LIMIT };

  return (
    <div className="p-4">
      <Row className="mb-4">
        <Col>
          <h3 className="mb-1">
            <FontAwesomeIcon icon={faCalendarDay} className="me-2 text-primary" />
            Wydarzenia z kalendarza
          </h3>
          <div className="text-muted">
            Pobranie wydarzeń z określonym statusem do wybranej daty (włącznie).
          </div>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Header className="bg-light fw-semibold">
          <FontAwesomeIcon icon={faFilter} className="me-2" />
          Filtry
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group controlId="status">
                  <Form.Label>Status wydarzeń</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Brak statusu</option>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="toDate">
                  <Form.Label>Do kiedy (włącznie)</Form.Label>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="search">
                  <Form.Label>Wyszukaj</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="np. nazwa obiektu lub fraza z opisu"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Szuka po nazwie obiektu i opisie zdarzenia.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="sortBy">
                  <Form.Label>Sortuj po</Form.Label>
                  <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    {SORT_BY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="sortDir">
                  <Form.Label>Kierunek</Form.Label>
                  <Form.Select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                    {SORT_DIR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col
                xs={12}
                className="d-flex flex-wrap gap-2 justify-content-end pt-3 align-items-center"
              >
                <div className="me-auto text-muted small">
                  Wybrane: <strong>{selectedStatusLabel}</strong> do <strong>{toDate}</strong>
                  {getSearchParam() ? (
                    <>
                      {" "}
                      | fraza: <strong>{getSearchParam()}</strong>
                    </>
                  ) : null}
                  {" "}
                  | sort: <strong>{SORT_BY_OPTIONS.find(o => o.value === sortBy)?.label}</strong>{" "}
                  <strong>{sortDir}</strong>
                </div>

                <Button type="submit" variant="primary" disabled={phase === "loading"}>
                  Pokaż wydarzenia
                </Button>

                <Button type="button" variant="outline-secondary" onClick={handleReset}>
                  <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <CalendarEventsResults
        phase={phase}
        error={error}
        data={resultsData}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
        privilege={privilege}
        onDeleteRow={handleDeleteRow}
        onEventChanged={handleEventChanged}
      />
    </div>
  );
}
